import useSMTDailyLogsCache from "@/hooks/useSMTDailyLogsCache";
import useAppMonitor from "@/services/AppMonitor";
import type { AddMealRequestBody } from "@/store/api/meals";
import { useAddMealMutation } from "@/store/api/meals";
import { useUploadSMTImageMutation } from "@/store/api/smt";
import { useAppSelector } from "@/store/hooks";
import { selectSMTPhoto } from "@/store/reducers/smt";
import { useSMTPhotoUploadDispatch } from "@/store/reducers/smtPhotoUpload";

interface SaveMealParams {
  onUploadComplete?: (success: boolean) => void;
  params: AddMealRequestBody;
}

interface UseAddSMTEntryResult {
  isLoading: boolean;
  saveMeal: (args: SaveMealParams) => Promise<null | string>;
}

interface HandleUploadPhotoParams {
  onUploadComplete?: (success: boolean) => void;
  smtEntryId: string;
}

const useAddSMTEntry = (): UseAddSMTEntryResult => {
  const { captureException } = useAppMonitor();
  const { updateApiSMTDailyLogsCache } = useSMTDailyLogsCache();
  const photo = useAppSelector(selectSMTPhoto);
  const { clearUpload, startUpload } = useSMTPhotoUploadDispatch();

  const [addMeal, addMealResult] = useAddMealMutation();
  const [uploadSMTImage] = useUploadSMTImageMutation();

  const { isLoading } = addMealResult;

  const handleUploadPhoto = async ({
    onUploadComplete,
    smtEntryId,
  }: HandleUploadPhotoParams): Promise<void> => {
    if (!photo) return;

    // Notify the UI that the upload has started so it can show an uploading state if desired
    startUpload(smtEntryId, photo);
    uploadSMTImage({
      file: photo,
      smtEntryId,
    })
      .unwrap()
      .then(async (uploadRes) => {
        if (!uploadRes.fileServiceKey) {
          throw new Error("Photo upload failed - no file service key returned");
        }

        onUploadComplete?.(true);
      })
      .catch((error) => {
        captureException(error);
        onUploadComplete?.(false);
      })
      .finally(() => {
        clearUpload();
      });
  };

  const saveMeal = async ({
    onUploadComplete,
    params: mealRequestBody,
  }: SaveMealParams): Promise<null | string> => {
    try {
      // Step 1: Create meal
      const response = await addMeal(mealRequestBody).unwrap();

      if (!response.smtEntryId) {
        throw new Error("Failed to add meal - no smt entry id returned");
      }

      // Step 2: Update SMT daily logs cache immediately with the new meal entry (without photo) so the UI can reflect the new entry right away, even while the photo is still uploading in the background
      const { occurredAt, patientExternalId: patientId } = mealRequestBody;
      updateApiSMTDailyLogsCache({ occurredAt, patientId });

      // Step 3: Upload photo in background if present
      void handleUploadPhoto({
        onUploadComplete,
        smtEntryId: response.smtEntryId,
      });

      // Step 4: Optimistically return the new SMT entry ID so the UI can update immediately, even while the photo is still uploading
      return response.smtEntryId;
    } catch (error) {
      captureException(error);
      return null;
    }
  };

  return {
    isLoading,
    saveMeal,
  };
};

export default useAddSMTEntry;
